import React, { createContext, useContext, useEffect, useState } from 'react';
import { Amplify } from 'aws-amplify';
import { Hub } from 'aws-amplify/utils';
import { getCurrentUser, signOut, fetchAuthSession } from 'aws-amplify/auth';
import { awsConfig } from '../config/aws-config';

// Configure Amplify
Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: awsConfig.Auth.userPoolId,
      userPoolClientId: awsConfig.Auth.userPoolWebClientId,
      loginWith: {
        oauth: {
          domain: awsConfig.Auth.oauth.domain,
          scopes: awsConfig.Auth.oauth.scope,
          redirectSignIn: [awsConfig.Auth.oauth.redirectSignIn],
          redirectSignOut: [awsConfig.Auth.oauth.redirectSignOut],
          responseType: awsConfig.Auth.oauth.responseType,
        }
      }
    }
  }
});

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    checkUser();

    // Listen for auth events (like OAuth callback completion)
    const hubListener = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signInWithRedirect':
          console.log('Sign in with redirect successful');
          checkUser();
          break;
        case 'signInWithRedirect_failure':
          console.error('Sign in with redirect failed:', payload.data);
          setError(payload.data?.message || 'Sign in failed');
          setLoading(false);
          break;
        case 'customOAuthState':
          console.log('Custom OAuth state:', payload.data);
          break;
      }
    });

    return () => hubListener();
  }, []);

  async function checkUser() {
    try {
      console.log('Attempting to get current user...');
      const currentUser = await getCurrentUser();
      console.log('Current user:', currentUser);
      setUser(currentUser);
      setError(null);
    } catch (err) {
      console.log('Not authenticated:', err);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }

  async function handleSignOut() {
    try {
      await signOut();
      setUser(null);
    } catch (err) {
      console.error('Error signing out:', err);
      setError(err.message);
    }
  }

  async function getAuthToken() {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString();
    } catch (err) {
      console.error('Error getting auth token:', err);
      return null;
    }
  }

  const value = {
    user,
    loading,
    error,
    signOut: handleSignOut,
    checkUser,
    getAuthToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
